'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'

import TokenCard from '@/components/Charts/TokenCard'

import { TokenCardsCarouselProps } from './types'

// Internal navigation icons
const ArrowForwardIcon: React.FC<{ color?: string; className?: string }> = ({
  color = '#A0A9BE',
  className = '',
}) => (
  <svg
    className={className}
    fill="none"
    height="32"
    viewBox="0 0 32 32"
    width="32"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.959 8.62574C18.3494 8.23532 18.9825 8.23554 19.373 8.62574L26.04 15.2927L26.1064 15.366C26.2522 15.5439 26.333 15.7678 26.333 15.9998C26.3329 16.2649 26.2275 16.5193 26.04 16.7068L19.373 23.3728C18.9825 23.7632 18.3495 23.7633 17.959 23.3728C17.569 22.9823 17.5688 22.3491 17.959 21.9587L22.9189 16.9998H6.66602C6.11414 16.9994 5.66619 16.5517 5.66602 15.9998C5.66602 15.4477 6.11403 15.0001 6.66602 14.9998H22.9189L17.959 10.0398C17.5689 9.64936 17.5689 9.01618 17.959 8.62574Z"
      fill={color}
    />
  </svg>
)

const ArrowBackwardIcon: React.FC<{ color?: string; className?: string }> = ({
  color = '#A0A9BE',
  className = '',
}) => (
  <svg
    className={className}
    fill="none"
    height="32"
    viewBox="0 0 32 32"
    width="32"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12.627 8.62574C13.0175 8.23554 13.6506 8.23532 14.041 8.62574C14.4311 9.01618 14.4311 9.64936 14.041 10.0398L9.08105 14.9998H25.334C25.886 15.0001 26.334 15.4477 26.334 15.9998C26.3338 16.5517 25.8859 16.9994 25.334 16.9998H9.08105L14.041 21.9587C14.4312 22.3491 14.431 22.9823 14.041 23.3728C13.6505 23.7633 13.0175 23.7632 12.627 23.3728L5.95996 16.7068C5.7725 16.5193 5.66708 16.2649 5.66699 15.9998C5.66699 15.7678 5.74782 15.5439 5.89355 15.366L5.95996 15.2927L12.627 8.62574Z"
      fill={color}
    />
  </svg>
)

interface DotsProps {
  totalDots: number
  currentIndex: number
  onDotClick?: (index: number) => void
}

const Dots: React.FC<DotsProps> = ({ totalDots, currentIndex, onDotClick }) => {
  return (
    <div className="flex cursor-pointer justify-center gap-[6px]">
      {Array.from({ length: totalDots }, (_, i) => (
        <div
          aria-current={i === currentIndex}
          className={twMerge(
            'h-1 w-2 rounded-[12px] transition-colors',
            i === Math.floor(currentIndex) ? 'bg-white' : 'bg-[#323C52]',
          )}
          key={i}
          onClick={() => onDotClick?.(i)}
        />
      ))}
    </div>
  )
}

interface TabButtonProps {
  className?: string
  isActive: boolean
  label: string
  icon?: React.ReactNode | ((isActive: boolean) => React.ReactNode)
  onClick: () => void
}

const TabButton: React.FC<TabButtonProps> = ({
  className = '',
  isActive,
  label,
  icon,
  onClick,
}) => {
  const iconElement = typeof icon === 'function' ? icon(isActive) : icon

  return (
    <button
      className={twMerge(
        'flex h-8 cursor-pointer items-center justify-center gap-1 rounded-[40px] text-[12px] font-semibold transition-colors',
        isActive
          ? 'border border-[#1199FA] text-[#92D1FF]'
          : 'border border-white/10 bg-[#151D32] text-[#A0A9BE]',
        className,
      )}
      style={isActive ? { backgroundColor: 'rgba(17, 153, 250, 0.20)' } : {}}
      onClick={onClick}
    >
      {iconElement}
      {label}
    </button>
  )
}

const TokenCardsCarousel: React.FC<TokenCardsCarouselProps> = ({
  tokens,
  tabs,
  activeTabId,
  onTabChange,
  currency = 'USD',
  deviceType = 'desktop',
  locale = 'en',
  buyButtonText = 'Buy',
  size = 'lg',
  cardsPerPage,
  showNavigationArrows = true,
  showDots = true,
  className = '',
  containerClassName = '',
  tabsClassName = '',
}) => {
  const isDesktop = deviceType === 'desktop'
  const isMobile = deviceType === 'mobile'
  const isSmall = size === 'sm'
  const [currentPage, setCurrentPage] = useState(0)
  const [displayPage, setDisplayPage] = useState(0)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const isAutoScrollRef = useRef(false)

  // Calculate default cards per page based on size
  // Small tickers (320px) can fit more cards than large tickers (410px)
  const getDefaultCardsPerPage = () => {
    if (isMobile) {
      return 1 // Mobile always shows 1 card at a time
    }
    // Desktop: small tickers fit 4 cards, large tickers fit 3 cards
    return isSmall ? 4 : 3
  }

  const CARDS_PER_PAGE =
    cardsPerPage?.[isMobile ? 'mobile' : 'desktop'] ?? getDefaultCardsPerPage()
  const totalPages = Math.ceil(tokens.length / CARDS_PER_PAGE)

  const getScrollMetrics = useCallback(() => {
    if (isSmall) {
      return {
        cardWidth: 320,
        gap: 16,
      }
    }
    return {
      cardWidth: isDesktop ? 410 : 288,
      gap: isDesktop ? 24 : 16,
    }
  }, [isSmall, isDesktop])

  // Reset to first page when tokens change
  useEffect(() => {
    setCurrentPage(0)
    setDisplayPage(0)
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0
    }
  }, [tokens.length])

  // Handle scroll events
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) return

    const handleScroll = () => {
      if (isAutoScrollRef.current) return

      const { cardWidth, gap } = getScrollMetrics()
      const scrollLeft = scrollContainer.scrollLeft
      const maxScroll =
        scrollContainer.scrollWidth - scrollContainer.clientWidth

      if (scrollLeft >= maxScroll - 10) {
        setCurrentPage(totalPages - 1)
        setDisplayPage(totalPages - 1)
      } else {
        const pageIndex = Math.round(
          scrollLeft / (CARDS_PER_PAGE * (cardWidth + gap)),
        )
        setCurrentPage(pageIndex)
        setDisplayPage(pageIndex)
      }
    }

    scrollContainer.addEventListener('scroll', handleScroll)
    return () => scrollContainer.removeEventListener('scroll', handleScroll)
  }, [totalPages, CARDS_PER_PAGE, getScrollMetrics])

  const scrollToPage = (pageIndex: number) => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) return

    isAutoScrollRef.current = true
    const { cardWidth, gap } = getScrollMetrics()

    let scrollPosition = pageIndex * CARDS_PER_PAGE * (cardWidth + gap)
    if (pageIndex === totalPages - 1) {
      scrollPosition = scrollContainer.scrollWidth - scrollContainer.clientWidth
    }

    scrollContainer.scrollTo({
      left: scrollPosition,
      behavior: 'smooth',
    })

    setTimeout(() => {
      isAutoScrollRef.current = false
    }, 500)
  }

  const handleNext = () => {
    if (currentPage < totalPages - 1) {
      const nextPage = currentPage + 1
      setCurrentPage(nextPage)
      setDisplayPage(nextPage)
      scrollToPage(nextPage)
    }
  }

  const handlePrev = () => {
    if (currentPage > 0) {
      const prevPage = currentPage - 1
      setCurrentPage(prevPage)
      setDisplayPage(prevPage)
      scrollToPage(prevPage)
    }
  }

  const handleDotClick = (index: number) => {
    setCurrentPage(index)
    setDisplayPage(index)
    scrollToPage(index)
  }

  const hasTabs = tabs && tabs.length > 0

  return (
    <div className={twMerge('w-full', className)}>
      {/* Tabs Section (optional) */}
      {hasTabs && (
        <div
          className={twMerge(
            'mb-6 flex justify-between px-4 lg:px-[80px]',
            tabsClassName,
          )}
        >
          <div className="flex gap-[18px]">
            {tabs.map(tab => (
              <TabButton
                className="w-auto px-4"
                icon={tab.icon}
                isActive={activeTabId === tab.id}
                key={tab.id}
                label={tab.label}
                onClick={() => onTabChange?.(tab.id)}
              />
            ))}
          </div>
          {isDesktop && showNavigationArrows && totalPages > 1 && (
            <div className="flex gap-4">
              <button
                className="cursor-pointer disabled:opacity-40"
                disabled={displayPage === 0}
                onClick={handlePrev}
              >
                <ArrowBackwardIcon color="#A0A9BE" />
              </button>
              <button
                className="cursor-pointer disabled:opacity-40"
                disabled={displayPage === totalPages - 1}
                onClick={handleNext}
              >
                <ArrowForwardIcon color="#A0A9BE" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Navigation Arrows (when tabs are not shown) */}
      {!hasTabs && isDesktop && showNavigationArrows && totalPages > 1 && (
        <div className="mb-6 flex justify-end px-4 lg:px-[80px]">
          <div className="flex gap-4">
            <button
              className="cursor-pointer disabled:opacity-40"
              disabled={displayPage === 0}
              onClick={handlePrev}
            >
              <ArrowBackwardIcon color="#A0A9BE" />
            </button>
            <button
              className="cursor-pointer disabled:opacity-40"
              disabled={displayPage === totalPages - 1}
              onClick={handleNext}
            >
              <ArrowForwardIcon color="#A0A9BE" />
            </button>
          </div>
        </div>
      )}

      {/* Token Cards Carousel */}
      <div
        className={twMerge(
          'no-scrollbar mb-8 overflow-x-auto px-4 lg:mb-10 lg:px-[80px]',
          containerClassName,
        )}
        ref={scrollContainerRef}
      >
        <div
          className={twMerge(
            'flex flex-nowrap gap-4 lg:gap-6',
            isSmall ? 'min-h-[120px]' : 'min-h-[312px] lg:min-h-[430px]',
          )}
          style={{ width: 'max-content' }}
        >
          {tokens.map(token => (
            <TokenCard
              buyButtonText={buyButtonText}
              currency={currency}
              deviceType={deviceType}
              key={token.slug}
              locale={locale}
              size={size}
              token={token}
            />
          ))}
        </div>
      </div>

      {/* Pagination Dots */}
      {showDots && totalPages > 1 && (
        <Dots
          currentIndex={displayPage}
          totalDots={totalPages}
          onDotClick={handleDotClick}
        />
      )}
    </div>
  )
}

export default TokenCardsCarousel
export type { TokenCardsCarouselProps, Tab } from './types'
